import React from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  Stack
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import MedicalServicesRoundedIcon from '@mui/icons-material/MedicalServicesRounded';
import BiotechRoundedIcon from '@mui/icons-material/BiotechRounded';
import EngineeringRoundedIcon from '@mui/icons-material/EngineeringRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { homeTranslations } from '../../locales/homeTranslations';
import './Home.css';

// Academic Research Illustration Component using hero-right.png
const ResearchHeroIllustration = () => {
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box
        component="img"
        src="/hero-right.png"
        alt="ARC Scientific Publishing & Research Impact"
        sx={{
          width: '100%',
          height: 'auto',
          maxWidth: { xs: '380px', sm: '460px', md: '560px', lg: '640px', xl: '700px' },
          maxHeight: { xs: '380px', sm: '460px', md: '540px', lg: '620px', xl: '680px' },
          objectFit: 'contain',
          filter: 'drop-shadow(0 24px 48px rgba(0, 0, 0, 0.5)) drop-shadow(0 0 35px rgba(5, 219, 242, 0.28))',
          animation: 'arcHeroImgFloat 6s ease-in-out infinite alternate',
          '@keyframes arcHeroImgFloat': {
            '0%': { transform: 'translateY(0px)' },
            '100%': { transform: 'translateY(-10px)' },
          },
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
          },
        }}
      />
    </Box>
  );
};

const Home = () => {
  const { currentLanguage } = useLanguage();
  const t = homeTranslations[currentLanguage] || homeTranslations.en;

  const faqs = t.faqs.list;
  const benefits = t.benefits.list;

  const areaIcons = [
    { icon: <MedicalServicesRoundedIcon sx={{ fontSize: 28, color: '#027b9a' }} />, color: '#027b9a', bgLight: 'rgba(2, 123, 154, 0.08)' },
    { icon: <BiotechRoundedIcon sx={{ fontSize: 28, color: '#059669' }} />, color: '#059669', bgLight: 'rgba(5, 150, 105, 0.08)' },
    { icon: <EngineeringRoundedIcon sx={{ fontSize: 28, color: '#4D49BF' }} />, color: '#4D49BF', bgLight: 'rgba(77, 73, 191, 0.08)' },
    { icon: <PublicRoundedIcon sx={{ fontSize: 28, color: '#16a34a' }} />, color: '#16a34a', bgLight: 'rgba(22, 163, 74, 0.08)' },
    { icon: <SchoolRoundedIcon sx={{ fontSize: 28, color: '#d97706' }} />, color: '#d97706', bgLight: 'rgba(217, 119, 6, 0.08)' },
    { icon: <HubRoundedIcon sx={{ fontSize: 28, color: '#7c3aed' }} />, color: '#7c3aed', bgLight: 'rgba(124, 58, 237, 0.08)' },
    { icon: <CategoryRoundedIcon sx={{ fontSize: 28, color: '#4D49BF' }} />, color: '#4D49BF', bgLight: 'rgba(77, 73, 191, 0.08)' },
    { icon: <TrendingUpRoundedIcon sx={{ fontSize: 28, color: '#db2777' }} />, color: '#db2777', bgLight: 'rgba(219, 39, 119, 0.08)' }
  ];

  const areasCovered = t.areasCovered.items.map((item, index) => ({
    ...item,
    icon: areaIcons[index]?.icon || <AutoAwesomeRoundedIcon sx={{ fontSize: 28, color: '#4D49BF' }} />,
    color: areaIcons[index]?.color || '#4D49BF',
    bgLight: areaIcons[index]?.bgLight || 'rgba(77, 73, 191, 0.08)'
  }));

  // Split benefits into two columns
  const half = Math.ceil(benefits.length / 2);
  const benefitsCol1 = benefits.slice(0, half);
  const benefitsCol2 = benefits.slice(half);

  return (
    <Box>
      {/* Hero Section */}
      <Box id="home" className="arc-hero-wrapper">
        {/* Background scientific dot grid & animated ambient glowing orbs */}
        <Box className="arc-hero-grid-pattern" />
        <Box className="arc-glow-orb-1" />
        <Box className="arc-glow-orb-2" />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: { xs: 4, sm: 3, md: 4, lg: 6 },
              width: '100%',
            }}
          >
            {/* Left Content */}
            <Box
              sx={{
                flex: { xs: '1 1 100%', sm: '1 1 54%', md: '1 1 52%', lg: '1 1 50%' },
                width: { xs: '100%', sm: '54%', md: '52%', lg: '50%' },
                minWidth: 0,
                textAlign: { xs: 'center', sm: 'left' }
              }}
            >
              {/* Trust Badge */}
              <Box sx={{ mb: 2, display: 'flex', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                <Box className="arc-trust-pill">
                  <span className="arc-live-indicator" />
                  <span>{t.hero.trustPill}</span>
                </Box>
              </Box>

              {/* Main Headline */}
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '1.75rem', sm: '1.9rem', md: '2.4rem', lg: '3.1rem', xl: '3.5rem' },
                  fontWeight: 800,
                  lineHeight: { xs: 1.22, md: 1.15 },
                  letterSpacing: '-0.02em',
                  color: '#ffffff',
                  mb: 2,
                  width: '100%',
                  mx: { xs: 'auto', sm: 0 }
                }}
              >
                {t.hero.titleMain} <span className="arc-gradient-text">{t.hero.titleAccent}</span> {t.hero.titleEnd}
              </Typography>

              {/* Subtitle */}
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: '0.875rem', sm: '0.9rem', md: '1rem', lg: '1.1rem' },
                  lineHeight: 1.6,
                  color: 'rgba(226, 232, 240, 0.9)',
                  width: '100%',
                  mb: 3,
                  mx: { xs: 'auto', sm: 0 }
                }}
              >
                {t.hero.subtitle}
              </Typography>

              {/* Action Buttons */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                justifyContent={{ xs: 'center', sm: 'flex-start' }}
                alignItems="center"
                sx={{ mb: 3.5 }}
              >
                <Button
                  component={Link}
                  to="/contact"
                  className="arc-hero-primary-btn"
                  endIcon={<ArrowForwardRoundedIcon />}
                  sx={{ width: { xs: '100%', sm: 'auto' }, py: { xs: 1.2, sm: 1.4 } }}
                >
                  {t.hero.contactBtn}
                </Button>

                <Button
                  component={Link}
                  to="/journals"
                  className="arc-hero-secondary-btn"
                  startIcon={<MenuBookRoundedIcon />}
                  sx={{ width: { xs: '100%', sm: 'auto' }, py: { xs: 1.2, sm: 1.4 } }}
                >
                  {t.hero.browseBtn}
                </Button>
              </Stack>

              {/* Key Trust Stats Grid */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
                  gap: { xs: 1.2, sm: 1.5, lg: 2 },
                  width: '100%',
                  mx: { xs: 'auto', sm: 0 }
                }}
              >
                <Box className="arc-stat-card">
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#05DBF2', fontSize: { xs: '1rem', sm: '1.05rem', md: '1.2rem' } }}>
                    10+
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.72rem', fontWeight: 500 }}>
                    {t.hero.stats.journals}
                  </Typography>
                </Box>
                <Box className="arc-stat-card">
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#34d399', fontSize: { xs: '1rem', sm: '1.05rem', md: '1.2rem' } }}>
                    140+
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.72rem', fontWeight: 500 }}>
                    {t.hero.stats.countries}
                  </Typography>
                </Box>
                <Box className="arc-stat-card">
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#a5b4fc', fontSize: { xs: '1rem', sm: '1.05rem', md: '1.2rem' } }}>
                    2-4 Wks
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.72rem', fontWeight: 500 }}>
                    {t.hero.stats.review}
                  </Typography>
                </Box>
                <Box className="arc-stat-card">
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#fbbf24', fontSize: { xs: '1rem', sm: '1.05rem', md: '1.2rem' } }}>
                    100%
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.72rem', fontWeight: 500 }}>
                    {t.hero.stats.openAccess}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Right Content - Custom Illustration Image */}
            <Box
              sx={{
                flex: { xs: '1 1 100%', sm: '1 1 46%', md: '1 1 48%', lg: '1 1 50%' },
                width: { xs: '100%', sm: '46%', md: '48%', lg: '50%' },
                minWidth: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <ResearchHeroIllustration />
            </Box>
          </Box>
        </Container>
      </Box>

      {/* About Us Section */}
      <Box id="about" sx={{
        pt: 3,
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&:before, &:after': {
          content: '""',
          position: 'absolute',
          borderRadius: '50%',
          filter: 'blur(80px)',
          opacity: 0.6,
          zIndex: 0,
        },
        '&:before': {
          width: '400px',
          height: '400px',
          background: 'linear-gradient(130deg, #4D49BF, rgba(5, 219, 242, 0.2))',
          top: '-100px',
          left: '-100px',
          '@media (max-width: 900px)': {
            width: '300px',
            height: '300px',
          },
        },
        '&:after': {
          width: '500px',
          height: '500px',
          background: 'linear-gradient(130deg, #05DBF2, rgba(77, 73, 191, 0.2))',
          bottom: '-150px',
          right: '-100px',
          '@media (max-width: 900px)': {
            width: '350px',
            height: '350px',
          },
        },
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{
            mb: 6,
            textAlign: 'center',
            position: 'relative',
            display: 'inline-block',
            left: '50%',
            transform: 'translateX(-50%)'
          }}>
            <Typography variant="h3" component="h2" sx={{
              color: '#2c3e50',
              fontWeight: 800,
              fontSize: '2.5rem',
              display: 'inline-block',
            }}>
              {t.about.sectionTitle}
            </Typography>
          </Box>

          <Box sx={{
            maxWidth: '1000px',
            mx: 'auto',
            mb: 6,
            p: { xs: 3, md: 5 },
            background: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(10px)',
            borderRadius: '20px',
            border: '1px solid rgba(255, 255, 255, 0.5)',
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.1)',
            position: 'relative',
            overflow: 'hidden',
            '&:before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #4D49BF, #05DBF2)',
            }
          }}>
            <Typography variant="h5" component="p" sx={{
              color: '#2d3748',
              lineHeight: 1.8,
              mb: 4,
              fontSize: { xs: '1.1rem', md: '1.25rem' },
              fontWeight: 500,
              textAlign: 'center',
              position: 'relative',
              zIndex: 1,
            }}>
              {t.about.highlight}
            </Typography>

            <Box sx={{
              width: '100px',
              height: '2px',
              background: 'linear-gradient(90deg, #4D49BF, #05DBF2)',
              mx: 'auto',
              mb: 4,
              opacity: 0.5
            }} />

            <Typography variant="body1" sx={{
              color: '#4a5568',
              lineHeight: 1.8,
              fontSize: { xs: '1rem', md: '1.1rem' },
              textAlign: 'center',
              position: 'relative',
              zIndex: 1,
              '&:not(:last-child)': {
                mb: 3,
              }
            }}>
              {t.about.description1}
            </Typography>

            <Typography variant="body1" sx={{
              color: '#4a5568',
              lineHeight: 1.8,
              fontSize: { xs: '1rem', md: '1.1rem' },
              textAlign: 'center',
              position: 'relative',
              zIndex: 1,
            }}>
              {t.about.description2}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Mission & Vision Section */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        backgroundColor: 'white',
        py: 0
      }}>
        {/* First Block - Our Mission */}
        <Box sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          backgroundImage: 'radial-gradient(white 1px, skyblue 1px)',
          backgroundSize: '20px 20px',
        }}>
          {/* Content on the left */}
          <Box sx={{
            width: { xs: '100%', md: '50%' },
            p: { xs: 4, md: 8 },
            zIndex: 2,
            color: 'black',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            backgroundColor: 'transparent',
            backgroundImage: { xs: 'radial-gradient(white 1px, skyblue 1px)', md: 'none' },
            backgroundSize: '20px 20px'
          }}>
            <Typography variant="h3" component="h2" sx={{
              fontWeight: 700,
              mb: 3,
              fontSize: { xs: '2rem', md: '2.5rem' },
              textAlign: { xs: 'center', md: 'left' }
            }}>
              {t.mission.title}
            </Typography>

            <Typography
              variant="body1"
              component="ul"
              sx={{
                mb: 4,
                pl: 3,
                fontSize: '1.1rem',
                lineHeight: 1.7,
                textAlign: { xs: 'left', md: 'left' },
              }}
            >
              {t.mission.points.map((point, index) => (
                <Box component="li" key={index} sx={{ mb: index === t.mission.points.length - 1 ? 0 : 1 }}>
                  {point}
                </Box>
              ))}
            </Typography>
          </Box>
          {/* Image on the right */}
          <Box sx={{
            width: { xs: '100%', md: '50%' },
            height: { xs: '300px', md: 'auto' },
            minHeight: { md: '500px' },
            '& img': {
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }
          }}>
            <img
              src="/mission.png"
              alt="Our Mission - Academic Publishing"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Box>
        </Box>

        {/* Second Block - Our Vision */}
        <Box sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column-reverse', md: 'row' },
          backgroundColor: 'white'
        }}>
          {/* Image on the left */}
          <Box sx={{
            width: { xs: '100%', md: '50%' },
            height: { xs: '300px', md: 'auto' },
            minHeight: { md: '500px' },
            '& img': {
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }
          }}>
            <img
              src="/vision.png"
              alt="Our Vision - Academic Excellence"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Box>

          {/* Content on the right */}
          <Box sx={{
            width: { xs: '100%', md: '50%' },
            p: { xs: 4, md: 8 },
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            backgroundColor: { xs: 'rgba(255, 255, 255, 0.95)', md: 'transparent' }
          }}>
            <Typography variant="h3" component="h2" sx={{
              fontWeight: 700,
              mb: 3,
              color: '#2c3e50',
              fontSize: { xs: '2rem', md: '2.5rem' },
              textAlign: { xs: 'center', md: 'left' }
            }}>
              {t.vision.title}
            </Typography>

            <Typography
              variant="body1"
              component="ul"
              sx={{
                mb: 4,
                color: '#333',
                fontSize: '1.1rem',
                lineHeight: 1.7,
                textAlign: 'left',
                pl: 3,
              }}
            >
              {t.vision.points.map((point, index) => (
                <Box component="li" key={index} sx={{ mb: index === t.vision.points.length - 1 ? 0 : 1 }}>
                  {point}
                </Box>
              ))}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Areas We Cover Section */}
      <Box
        id="areas-we-cover"
        sx={{
          py: { xs: 8, md: 10 },
          background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 50%, #f1f5f9 100%)',
          position: 'relative',
          overflow: 'hidden',
          borderTop: '1px solid #e2e8f0',
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        {/* Decorative background ambient glows */}
        <Box
          sx={{
            position: 'absolute',
            top: '15%',
            right: '-5%',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(56, 189, 248, 0.08) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '10%',
            left: '-5%',
            width: '350px',
            height: '350px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(99, 102, 241, 0.08) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Section Header */}
          <Box sx={{ textAlign: 'center', maxWidth: '850px', mx: 'auto', mb: { xs: 5, md: 7 } }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.6,
                borderRadius: '50px',
                bgcolor: 'rgba(77, 73, 191, 0.08)',
                color: '#4D49BF',
                fontSize: '0.85rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                mb: 2,
              }}
            >
              <AutoAwesomeRoundedIcon sx={{ fontSize: 16 }} />
              {t.areasCovered.badge}
            </Box>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2rem', sm: '2.4rem', md: '2.8rem' },
                color: '#0f172a',
                lineHeight: 1.2,
                mb: 2.5,
              }}
            >
              {t.areasCovered.titleMain} <span style={{ background: 'linear-gradient(90deg, #4D49BF, #05DBF2)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}></span>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#475569',
                fontSize: { xs: '1rem', md: '1.1rem' },
                lineHeight: 1.75,
              }}
            >
              {t.areasCovered.intro}
            </Typography>
          </Box>

          {/* Cards Grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)'
              },
              gap: { xs: 2.5, sm: 3 },
              mb: 6,
              width: '100%'
            }}
          >
            {areasCovered.map((area, index) => (
              <Box
                key={index}
                sx={{
                  height: '100%',
                  p: 3,
                  borderRadius: '16px',
                  bgcolor: '#ffffff',
                  border: '1px solid rgba(226, 232, 240, 0.8)',
                  boxShadow: '0 4px 16px rgba(15, 23, 42, 0.04)',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: '0 16px 32px rgba(15, 23, 42, 0.08)',
                    borderColor: area.color,
                    '& .area-icon-box': {
                      bgcolor: area.color,
                      color: '#ffffff',
                      transform: 'scale(1.05)',
                    },
                    '& .area-icon-box svg': {
                      color: '#ffffff',
                    },
                  },
                }}
              >
                {/* Top accent bar */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    bgcolor: area.color,
                    opacity: 0.85,
                  }}
                />

                {/* Icon */}
                <Box
                  className="area-icon-box"
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '12px',
                    bgcolor: area.bgLight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2.5,
                    transition: 'all 0.3s ease',
                  }}
                >
                  {area.icon}
                </Box>

                {/* Title */}
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.05rem',
                    color: '#0f172a',
                    lineHeight: 1.35,
                    mb: 1.5,
                  }}
                >
                  {area.title}
                </Typography>

                {/* Description */}
                <Typography
                  variant="body2"
                  sx={{
                    color: '#64748b',
                    fontSize: '0.88rem',
                    lineHeight: 1.65,
                    flexGrow: 1,
                  }}
                >
                  {area.description}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Bottom Statement Card */}
          <Box
            sx={{
              p: { xs: 3, sm: 4 },
              borderRadius: '18px',
              background: 'linear-gradient(135deg, #091224 0%, #132754 100%)',
              color: '#ffffff',
              boxShadow: '0 12px 30px rgba(19, 39, 84, 0.25)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Subtle glow orb */}
            <Box
              sx={{
                position: 'absolute',
                top: '-50%',
                right: '-10%',
                width: '300px',
                height: '300px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(56, 189, 248, 0.2) 0%, transparent 70%)',
              }}
            />

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'center' },
                justifyContent: 'space-between',
                gap: 3,
                position: 'relative',
                zIndex: 1,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: '#38bdf8',
                    fontSize: '1rem',
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <AutoAwesomeRoundedIcon sx={{ fontSize: 20 }} />
                  {t.areasCovered.statementTitle}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(226, 232, 240, 0.95)',
                    fontSize: { xs: '0.92rem', sm: '0.98rem' },
                    lineHeight: 1.7,
                    mb: 1.5,
                  }}
                >
                  {t.areasCovered.statementText1}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#94a3b8',
                    fontSize: '0.85rem',
                    display: 'block',
                    lineHeight: 1.6,
                  }}
                >
                  {t.areasCovered.statementText2}
                </Typography>
              </Box>

              <Box sx={{ flexShrink: 0, width: { xs: '100%', md: 'auto' } }}>
                <Button
                  component={Link}
                  to="/contact"
                  variant="contained"
                  endIcon={<ArrowForwardRoundedIcon />}
                  sx={{
                    width: { xs: '100%', md: 'auto' },
                    background: 'linear-gradient(135deg, #4D49BF 0%, #05DBF2 100%)',
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    px: 3.5,
                    py: 1.4,
                    borderRadius: '12px',
                    textTransform: 'none',
                    boxShadow: '0 4px 16px rgba(77, 73, 191, 0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #3f3ba6 0%, #02b6ca 100%)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {t.areasCovered.inquireBtn}
                </Button>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Our Mission & FAQ Section */}
      <Box sx={{ py: 5, backgroundColor: 'white' }}>
        <Container maxWidth="lg">
          {/* Our Journal Section */}
          <Box id="journal" sx={{ pt: { xs: 4, md: 4 }, pb: 5, backgroundColor: 'white', scrollMarginTop: '80px' }}>
            <Container maxWidth="lg">
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="h4" component="h2" sx={{
                  fontWeight: 700,
                  color: '#2c3e50',
                  fontSize: '2.5rem'
                }}>
                  {t.ourJournal.title}
                </Typography>

              </Box>

              <Box sx={{
                backgroundColor: 'white',
                p: 2,
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
              }}>

                <Typography variant="body1" sx={{
                  color: '#444',
                  mb: 3,
                  lineHeight: 1.8,
                  fontSize: '1.05rem'
                }}>
                  {t.ourJournal.intro}
                </Typography>

                <Typography variant="h6" component="h4" sx={{
                  fontWeight: 600,
                  mt: 4,
                  mb: 2,
                  color: '#4f46e5'
                }}>
                  {t.ourJournal.provideTitle}
                </Typography>

                <List sx={{
                  listStyleType: 'disc',
                  pl: 3,
                  mb: 3,
                  '& .MuiListItem-root': {
                    display: 'list-item',
                    p: 0,
                    mb: 1,
                    ml: 2,
                    color: '#444'
                  }
                }}>
                  {t.ourJournal.provideList.map((item, idx) => (
                    <ListItem key={idx}>
                      <Typography variant="body1" sx={{ lineHeight: 1.6 }}>{item}</Typography>
                    </ListItem>
                  ))}
                </List>

                <Typography variant="body1" sx={{
                  color: '#444',
                  lineHeight: 1.8,
                  fontSize: '1.05rem',
                  mt: 3,
                  fontStyle: 'italic'
                }}>
                  {t.ourJournal.closing}
                </Typography>
              </Box>

              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 6,
                alignItems: 'center',
                backgroundColor: '#f8f9fa',
                borderRadius: 3,
                p: { xs: 3, md: 5 },
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
              }}>
                {/* Journal Cover Image */}
                <Box sx={{
                  width: { xs: '100%', md: '300px' },
                  flexShrink: 0,
                  textAlign: 'center'
                }}>
                  <Box
                    component="img"
                    src="/cover-page.jpeg"
                    alt="Kidneys, Ukrainian Journal of Renal Medicine Cover"
                    sx={{
                      width: '100%',
                      maxWidth: '250px',
                      height: '350px',
                      objectFit: 'cover',
                      borderRadius: 2,
                      mx: 'auto',
                      boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                      mb: { xs: 3, md: 0 }
                    }}
                  />
                </Box>

                {/* Journal Details */}
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h5" component="h3" sx={{
                      display: 'inline',
                      fontWeight: 700,
                      color: '#4f46e5',
                      mr: 1
                    }}>
                      Kidneys
                    </Typography>
                    <Typography variant="subtitle1" component="span" sx={{
                      color: '#666',
                      fontSize: '0.95em',
                      fontWeight: 400
                    }}>
                      Ukrainian Journal of Renal Medicine
                    </Typography>
                  </Box>

                  <Typography variant="body1" sx={{
                    color: '#444',
                    mb: 3,
                    lineHeight: 1.7
                  }}>
                    The professional scientific and practical specialized peer-reviewed journal for doctors treating patients with kidney disease and their complications, highlighting the advances in nephrology, urology.
                  </Typography>

                  <Box sx={{
                    backgroundColor: 'white',
                    p: { xs: 2, sm: 3 },
                    borderRadius: 2,
                    boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <Box sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      justifyContent: 'space-between',
                      p: 3,
                      width: '100%',
                      gap: { xs: 2, sm: 4 },
                      flexWrap: 'wrap'
                    }}>
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        '& .MuiTypography-root': {
                          lineHeight: 1.4
                        }
                      }}>
                        <Typography variant="body2" sx={{
                          color: '#666',
                          fontWeight: 500,
                          fontSize: '0.85rem',
                          mb: 0.5
                        }}>
                          ISSN: 2307-1257 (Print) | 2307-1265 (Online) | Language: English |Frequency: Quarterly
                        </Typography>
                        <Typography variant="body1" sx={{
                          color: '#333',
                          fontWeight: 400,
                          fontSize: '1rem'
                        }}>
                          Founded: 2012 | DOI: 10.65327
                        </Typography>
                      </Box>

                      <Box sx={{
                        display: 'flex',
                        gap: 2,
                        flexWrap: 'wrap'
                      }}>
                        <Button
                          variant="contained"
                          href="https://kidneysjournal.com/index.php/journal/about/submissions"
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                          sx={{
                            bgcolor: '#4f46e5',
                            color: 'white',
                            textTransform: 'none',
                            fontSize: '0.9rem',
                            px: 2,
                            py: 1,
                            height: '40px',
                            '&:hover': {
                              bgcolor: '#4338ca',
                              boxShadow: '0 4px 8px rgba(79, 70, 229, 0.3)'
                            },
                            whiteSpace: 'nowrap',
                            minWidth: '120px',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            borderRadius: '8px',
                            transition: 'all 0.2s ease-in-out',
                            fontWeight: 500
                          }}
                        >
                          {t.ourJournal.submitPaper}
                        </Button>
                        <Button
                          variant="outlined"
                          href="https://kidneysjournal.com/index.php/journal"
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                          sx={{
                            borderColor: '#4f46e5',
                            color: '#4f46e5',
                            textTransform: 'none',
                            fontSize: '0.9rem',
                            px: 2,
                            py: 1,
                            height: '40px',
                            '&:hover': {
                              borderColor: '#4338ca',
                              color: '#4338ca',
                              bgcolor: 'rgba(79, 70, 229, 0.04)',
                              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
                            },
                            whiteSpace: 'nowrap',
                            minWidth: '120px',
                            borderRadius: '8px',
                            transition: 'all 0.2s ease-in-out',
                            fontWeight: 500,
                            borderWidth: '1.5px',
                            textDecoration: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {t.ourJournal.viewJournal}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Second Journal Entry - IJFRES */}
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 6,
                alignItems: 'center',
                backgroundColor: '#f8f9fa',
                borderRadius: 3,
                p: { xs: 3, md: 5 },
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                mt: 4
              }}>
                {/* Journal Cover Image */}
                <Box sx={{
                  width: { xs: '100%', md: '300px' },
                  flexShrink: 0,
                  textAlign: 'center'
                }}>
                  <Box
                    component="img"
                    src="/IJAN.jpeg"
                    alt="International Journal for Research in Applied And Natural Science"
                    sx={{
                      width: '100%',
                      maxWidth: '250px',
                      height: '350px',
                      objectFit: 'cover',
                      borderRadius: 2,
                      mx: 'auto',
                      boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                      mb: { xs: 3, md: 0 }
                    }}
                  />
                </Box>

                {/* Journal Details */}
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h5" component="h3" sx={{
                      display: 'inline',
                      fontWeight: 700,
                      color: '#4f46e5',
                      mr: 1
                    }}>

                    </Typography>
                    <Typography variant="h5" component="h3" sx={{
                      display: 'inline',
                      fontWeight: 700,
                      color: '#4f46e5',
                      mr: 1
                    }}>
                      International Journal for Research in Applied And Natural Science

                    </Typography>

                  </Box>

                  <Typography variant="body1" sx={{
                    color: '#444',
                    mb: 3,
                    lineHeight: 1.7
                  }}>
                    It is the is one of the best Open Access journals of Scholarly publishing that aims to publish the most complete and reliable source of information on discoveries and current developments.                  </Typography>

                  <Box sx={{
                    backgroundColor: 'white',
                    p: { xs: 2, sm: 3 },
                    borderRadius: 2,
                    boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <Box sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      justifyContent: 'space-between',
                      p: 3,
                      width: '100%',
                      gap: { xs: 2, sm: 4 },
                      flexWrap: 'wrap'
                    }}>
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        '& .MuiTypography-root': {
                          lineHeight: 1.4
                        }
                      }}>
                        <Typography variant="body2" sx={{
                          color: '#666',
                          fontWeight: 500,
                          fontSize: '0.85rem',
                          mb: 0.5
                        }}>
                          ISSN: 2208-2085 | Language: English | Frequency: Quarterly
                        </Typography>
                        <Typography variant="body1" sx={{
                          color: '#333',
                          fontWeight: 400,
                          fontSize: '1rem'
                        }}>
                          Founded: 2015
                        </Typography>
                      </Box>

                      <Box sx={{
                        display: 'flex',
                        gap: 2,
                        flexWrap: 'wrap'
                      }}>
                        <Button
                          variant="contained"
                          href="https://ijrans.com/index.php/ans/about/submissions"
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                          sx={{
                            bgcolor: '#4f46e5',
                            color: 'white',
                            textTransform: 'none',
                            fontSize: '0.9rem',
                            px: 2,
                            py: 1,
                            height: '40px',
                            '&:hover': {
                              bgcolor: '#4338ca',
                              boxShadow: '0 4px 8px rgba(79, 70, 229, 0.3)'
                            },
                            whiteSpace: 'nowrap',
                            minWidth: '120px',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            borderRadius: '8px',
                            transition: 'all 0.2s ease-in-out',
                            fontWeight: 500
                          }}
                        >
                          {t.ourJournal.submitPaper}
                        </Button>
                        <Button
                          variant="outlined"
                          href="https://ijrans.com/index.php/ans/about"
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                          sx={{
                            borderColor: '#4f46e5',
                            color: '#4f46e5',
                            textTransform: 'none',
                            fontSize: '0.9rem',
                            px: 2,
                            py: 1,
                            height: '40px',
                            '&:hover': {
                              borderColor: '#4338ca',
                              color: '#4338ca',
                              bgcolor: 'rgba(79, 70, 229, 0.04)',
                              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
                            },
                            whiteSpace: 'nowrap',
                            minWidth: '120px',
                            borderRadius: '8px',
                            transition: 'all 0.2s ease-in-out',
                            fontWeight: 500,
                            borderWidth: '1.5px',
                            textDecoration: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {t.ourJournal.viewJournal}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>



              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 6,
                alignItems: 'center',
                backgroundColor: '#f8f9fa',
                borderRadius: 3,
                p: { xs: 3, md: 5 },
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                mt: 4
              }}>
                {/* Journal Cover Image */}
                <Box sx={{
                  width: { xs: '100%', md: '300px' },
                  flexShrink: 0,
                  textAlign: 'center'
                }}>
                  <Box
                    component="img"
                    src="/ijfres.png"
                    alt="International Journal For Research In Educational Studies Cover"
                    sx={{
                      width: '100%',
                      maxWidth: '250px',
                      height: '350px',
                      objectFit: 'cover',
                      borderRadius: 2,
                      mx: 'auto',
                      boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                      mb: { xs: 3, md: 0 }
                    }}
                  />
                </Box>

                {/* Journal Details */}
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h5" component="h3" sx={{
                      display: 'inline',
                      fontWeight: 700,
                      color: '#4f46e5',
                      mr: 1
                    }}>

                    </Typography>


                    <Typography variant="h5" component="h3" sx={{
                      display: 'inline',
                      fontWeight: 700,
                      color: '#4f46e5',
                      mr: 1
                    }}>
                      International Journal for research in Educational Studies
                    </Typography>


                  </Box>

                  <Typography variant="body1" sx={{
                    color: '#444',
                    mb: 3,
                    lineHeight: 1.7
                  }}>
                    A quarterly, peer-reviewed, open-access journal serving as an international platform for researchers in the field of education. Encourages original research, reviews, and case studies that contribute to the advancement of educational theory and practice.
                  </Typography>

                  <Box sx={{
                    backgroundColor: 'white',
                    p: { xs: 2, sm: 3 },
                    borderRadius: 2,
                    boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <Box sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      justifyContent: 'space-between',
                      p: 3,
                      width: '100%',
                      gap: { xs: 2, sm: 4 },
                      flexWrap: 'wrap'
                    }}>
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        '& .MuiTypography-root': {
                          lineHeight: 1.4
                        }
                      }}>
                        <Typography variant="body2" sx={{
                          color: '#666',
                          fontWeight: 500,
                          fontSize: '0.85rem',
                          mb: 0.5
                        }}>
                          ISSN: 2208-2115 | Language: English | Frequency: Quarterly
                        </Typography>
                        <Typography variant="body1" sx={{
                          color: '#333',
                          fontWeight: 400,
                          fontSize: '1rem'
                        }}>
                          Founded: 2017 | DOI: 10.53555
                        </Typography>
                      </Box>

                      <Box sx={{
                        display: 'flex',
                        gap: 2,
                        flexWrap: 'wrap'
                      }}>
                        <Button
                          variant="contained"
                          href="https://ijfres.com/index.php/es/about/submissions"
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                          sx={{
                            bgcolor: '#4f46e5',
                            color: 'white',
                            textTransform: 'none',
                            fontSize: '0.9rem',
                            px: 2,
                            py: 1,
                            height: '40px',
                            '&:hover': {
                              bgcolor: '#4338ca',
                              boxShadow: '0 4px 8px rgba(79, 70, 229, 0.3)'
                            },
                            whiteSpace: 'nowrap',
                            minWidth: '120px',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            borderRadius: '8px',
                            transition: 'all 0.2s ease-in-out',
                            fontWeight: 500
                          }}
                        >
                          {t.ourJournal.submitPaper}
                        </Button>
                        <Button
                          variant="outlined"
                          href="https://ijfres.com/index.php/es/index"
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                          sx={{
                            borderColor: '#4f46e5',
                            color: '#4f46e5',
                            textTransform: 'none',
                            fontSize: '0.9rem',
                            px: 2,
                            py: 1,
                            height: '40px',
                            '&:hover': {
                              borderColor: '#4338ca',
                              color: '#4338ca',
                              bgcolor: 'rgba(79, 70, 229, 0.04)',
                              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
                            },
                            whiteSpace: 'nowrap',
                            minWidth: '120px',
                            borderRadius: '8px',
                            transition: 'all 0.2s ease-in-out',
                            fontWeight: 500,
                            borderWidth: '1.5px',
                            textDecoration: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {t.ourJournal.viewJournal}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>


              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Button
                  component={Link}
                  to="/journals"
                  variant="contained"
                  sx={{
                    bgcolor: '#4f46e5',
                    color: '#fff',
                    textTransform: 'none',
                    px: 4,
                    py: 1.2,
                    borderRadius: '30px',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    boxShadow: '0 4px 12px rgba(79, 70, 229, 0.25)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: '#4338ca',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 18px rgba(79, 70, 229, 0.35)',
                    },
                  }}
                >
                  {t.ourJournal.moreJournals}
                </Button>
              </Box>
            </Container>
          </Box>




          {/* Our Journal Section */}





          {/* Publication Procedure Section */}
          <Box id="publication-procedure" sx={{
            py: 8,
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)',
            minHeight: '100vh',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            '&:before, &:after': {
              content: '""',
              position: 'absolute',
              width: '500px',
              height: '500px',
              borderRadius: '50%',
              zIndex: 0,
              filter: 'blur(80px)',
              opacity: 0.6,
            },
            '&:before': {
              background: 'linear-gradient(130deg, #4D49BF, rgba(255,255,255,0.2))',
              top: '10%',
              left: '-10%',
            },
            '&:after': {
              background: 'linear-gradient(130deg, #05DBF2, rgba(255,255,255,0.2))',
              bottom: '10%',
              right: '-10%',
            },
            '& .procedure-container': {
              width: '100%',
              maxWidth: '100%',
              margin: 0,
              // padding: '0 24px',
              position: 'relative',
              zIndex: 1
            },
            '& .procedure-title': {
              textAlign: 'center',
              marginBottom: '60px',
              '& h2': {
                fontSize: { xs: '2rem', md: '2.5rem' },
                fontWeight: 700,
                color: '#1C1C22',
                marginBottom: '15px'
              },
              '& p': {
                color: '#58576B',
                fontSize: '1.1rem',
                maxWidth: '700px',
                margin: '0 auto',
                lineHeight: 1.6
              }
            },
            '& .procedure-steps': {
              display: 'flex',
              flexWrap: 'wrap',
              margin: '0 -15px',
              justifyContent: 'center',
              '@media (max-width: 767px)': {
                flexDirection: 'column',
                alignItems: 'center'
              }
            },
            '& .procedure-step': {
              flex: '1',
              minWidth: '300px',
              maxWidth: '350px',
              margin: '0 15px 30px',
              position: 'relative',
              background: 'linear-gradient(130deg, rgba(251, 251, 254, 0.6), rgba(251, 251, 254, 0.2))',
              backdropFilter: 'blur(10px)',
              color: '#1C1C22',
              padding: '2.5rem 1.5rem',
              border: '1px solid rgba(255, 255, 255, 0.5)',
              boxShadow: 'inset 2px 2px 1px rgba(251, 251, 254, 0.3), inset -2px -2px 1px rgba(251, 251, 254, 0.2)',
              borderRadius: '1.5rem',
              transition: 'all 0.4s ease',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
              },

            },
            '& .step-number': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '70px',
              height: '70px',
              borderRadius: '50%',
              background: 'linear-gradient(130deg, rgba(77, 73, 191, 0.8), rgba(5, 219, 242, 0.8))',
              color: '#fff',
              fontSize: '1.8rem',
              fontWeight: 'bold',
              margin: '0 auto 1.5rem',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease'
            },
            '& .step-title': {
              fontSize: '1.25rem',
              fontWeight: 600,
              marginBottom: '15px',
              textAlign: 'center',
              color: 'inherit'
            },
            '& .step-description': {
              fontSize: '0.95rem',
              lineHeight: 1.6,
              color: '#58576B',
              textAlign: 'center',
              transition: 'color 0.3s ease'
            }
          }}>
            <Container maxWidth="lg">
              <Box className="procedure-title">
                <Typography variant="h2" component="h2" sx={{ color: '#2c3e50' }}>
                  {t.procedure.title}
                </Typography>
                <Typography variant="body1">
                  {t.procedure.subtitle}
                </Typography>
              </Box>

              <Box className="procedure-steps">
                {t.procedure.steps.map((step, index) => (
                  <Box className={`procedure-step ${index === 0 ? 'active' : ''}`} key={index}>
                    <Box className="step-number">{index + 1}</Box>
                    <h3 className="step-title">{step.title}</h3>
                    <p className="step-description">{step.description}</p>
                  </Box>
                ))}
              </Box>

              <Box sx={{
                mt: 8,
                p: { xs: 3, md: 5 },
                background: 'linear-gradient(130deg, rgba(251, 251, 254, 0.6), rgba(251, 251, 254, 0.2))',
                backdropFilter: 'blur(10px)',
                borderRadius: '1.5rem',
                border: '1px solid rgba(255, 255, 255, 0.5)',
                boxShadow: 'inset 2px 2px 1px rgba(251, 251, 254, 0.3), inset -2px -2px 1px rgba(251, 251, 254, 0.2)',
                position: 'relative',
                overflow: 'hidden',
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  top: '-100px',
                  right: '-100px',
                  width: '300px',
                  height: '300px',
                  borderRadius: '50%',
                  background: 'linear-gradient(130deg, rgba(77, 73, 191, 0.1), rgba(5, 219, 242, 0.1))',
                  zIndex: 0,
                },
                '& > *': {
                  position: 'relative',
                  zIndex: 1,
                }
              }}>
                <Typography variant="h5" component="h3" sx={{
                  mb: 4,
                  color: '#1C1C22',
                  fontWeight: 700,
                  fontSize: '1.5rem',
                  textAlign: 'center',
                  position: 'relative',
                  '&:after': {
                    content: '""',
                    display: 'block',
                    width: '60px',
                    height: '3px',
                    background: 'linear-gradient(130deg, #4D49BF, #05DBF2)',
                    margin: '0.75rem auto 0',
                    borderRadius: '3px'
                  }
                }}>
                  {t.procedure.comprehensiveSupport?.title || 'Comprehensive Support Services'}
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    {(t.procedure.comprehensiveSupport?.col1 || [
                      'Language editing and proofreading services',
                      'Plagiarism checks and similarity reports',
                      'Formatting assistance to meet journal standards'
                    ]).map((service, index) => (
                      <Box key={index} sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        mb: 2.5,
                        p: 2,
                        borderRadius: '0.75rem',
                        background: 'rgba(255, 255, 255, 0.5)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateX(5px)',
                          boxShadow: '0 5px 15px rgba(0, 0, 0, 0.05)'
                        }
                      }}>
                        <Box sx={{
                          minWidth: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          background: 'linear-gradient(130deg, #4D49BF, #05DBF2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                          mt: '2px',
                          flexShrink: 0
                        }}>
                          <CheckCircleIcon sx={{ color: 'white', fontSize: '1rem' }} />
                        </Box>
                        <Typography sx={{ color: '#2d3748', lineHeight: 1.5 }}>{service}</Typography>
                      </Box>
                    ))}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    {(t.procedure.comprehensiveSupport?.col2 || [
                      'Expert guidance on responding to reviewer comments',
                      'Professional illustration and figure preparation',
                      'Post-publication impact tracking and analytics'
                    ]).map((service, index) => (
                      <Box key={index} sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        mb: 2.5,
                        p: 2,
                        borderRadius: '0.75rem',
                        background: 'rgba(255, 255, 255, 0.5)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateX(5px)',
                          boxShadow: '0 5px 15px rgba(0, 0, 0, 0.05)'
                        }
                      }}>
                        <Box sx={{
                          minWidth: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          background: 'linear-gradient(130deg, #4D49BF, #05DBF2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                          mt: '2px',
                          flexShrink: 0
                        }}>
                          <CheckCircleIcon sx={{ color: 'white', fontSize: '1rem' }} />
                        </Box>
                        <Typography sx={{ color: '#2d3748', lineHeight: 1.5 }}>{service}</Typography>
                      </Box>
                    ))}
                  </Grid>
                </Grid>
              </Box>
            </Container>
          </Box>

          {/* Benefits Section */}
          <Box id="benefits-of-publishing-paper-in-ARC-Publication-Services-LLC" sx={{
            py: 10,
            position: 'relative',
            overflow: 'hidden',
            scrollMarginTop: '100px',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)',
            '&:before, &:after': {
              content: '""',
              position: 'absolute',
              width: '500px',
              height: '500px',
              borderRadius: '50%',
              zIndex: 0,
              filter: 'blur(80px)',
              opacity: 0.6,
            },
            '&:before': {
              background: 'linear-gradient(130deg, #4D49BF, rgba(255,255,255,0.2))',
              top: '10%',
              left: '-10%',
            },
            '&:after': {
              background: 'linear-gradient(130deg, #05DBF2, rgba(255,255,255,0.2))',
              bottom: '10%',
              right: '-10%',
            },
          }}>
            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ textAlign: 'center', mb: 8 }}>
                <Typography variant="h4" component="h2" sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: '#1C1C22',
                  fontSize: { xs: '1.875rem', md: '2.25rem' },
                  lineHeight: 1.2,
                  position: 'relative',
                  display: 'inline-block',
                }}>
                  {t.benefits.title}
                </Typography>
                <Typography variant="body1" sx={{
                  maxWidth: '800px',
                  mx: 'auto',
                  color: '#58576B',
                  fontSize: '1.125rem',
                  lineHeight: 1.6,
                  mt: 3
                }}>
                  {t.benefits.subtitle}
                </Typography>
              </Box>

              <Box sx={{
                p: { xs: 3, md: 5 },
                background: 'linear-gradient(130deg, rgba(251, 251, 254, 0.6), rgba(251, 251, 254, 0.2))',
                backdropFilter: 'blur(10px)',
                borderRadius: '1.5rem',
                border: '1px solid rgba(255, 255, 255, 0.5)',
                boxShadow: 'inset 2px 2px 1px rgba(251, 251, 254, 0.3), inset -2px -2px 1px rgba(251, 251, 254, 0.2)',
                maxWidth: '1200px',
                mx: 'auto',
                position: 'relative',
                overflow: 'hidden',
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  top: '-50px',
                  right: '-50px',
                  width: '200px',
                  height: '200px',
                  borderRadius: '50%',
                  background: 'linear-gradient(130deg, rgba(77, 73, 191, 0.1), rgba(5, 219, 242, 0.1))',
                  zIndex: 0,
                },
                '& > *': {
                  position: 'relative',
                  zIndex: 1,
                }
              }}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: 4,
                  alignItems: 'flex-start',
                  maxWidth: '1200px',
                  mx: 'auto',
                  px: { xs: 0, md: 4 }
                }}>
                  {/* Left Column */}
                  <Box sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    width: '100%',
                    pr: { md: 4 },
                    borderRight: { md: '1px solid rgba(0, 0, 0, 0.1)' }
                  }}>
                    {benefitsCol1.map((benefit, index) => (
                      <Box key={index} sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 3,
                        width: '100%',
                        p: 2,
                        borderRadius: '0.75rem',
                        background: 'rgba(255, 255, 255, 0.5)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateX(5px)',
                          boxShadow: '0 5px 15px rgba(0, 0, 0, 0.05)'
                        }
                      }}>
                        <Box sx={{
                          flexShrink: 0,
                          width: 40,
                          height: 40,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '8px',
                          background: 'linear-gradient(130deg, #4D49BF, #05DBF2)',
                          color: 'white',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                        }}>
                          <CheckCircleIcon sx={{ fontSize: '1.25rem' }} />
                        </Box>
                        <Typography sx={{ color: '#2d3748', lineHeight: 1.5, fontSize: '1rem' }}>{benefit}</Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Right Column */}
                  <Box sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    width: '100%',
                    pl: { md: 4 }
                  }}>
                    {benefitsCol2.map((benefit, index) => (
                      <Box key={index} sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 3,
                        width: '100%',
                        p: 2,
                        borderRadius: '0.75rem',
                        background: 'rgba(255, 255, 255, 0.5)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateX(5px)',
                          boxShadow: '0 5px 15px rgba(0, 0, 0, 0.05)'
                        }
                      }}>
                        <Box sx={{
                          flexShrink: 0,
                          width: 40,
                          height: 40,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '8px',
                          background: 'linear-gradient(130deg, #4D49BF, #05DBF2)',
                          color: 'white',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                        }}>
                          <CheckCircleIcon sx={{ fontSize: '1.25rem' }} />
                        </Box>
                        <Typography sx={{ color: '#2d3748', lineHeight: 1.5, fontSize: '1rem' }}>{benefit}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Container>
          </Box>

          {/* Licenses and Indexing Section */}
          <Box sx={{ pt: 6, backgroundColor: 'white' }}>
            <Container maxWidth="lg">
              <Typography
                variant="h4"
                component="h2"
                sx={{
                  textAlign: 'center',
                  mb: 5,
                  fontWeight: 700,
                  color: '#2d3748',
                  fontSize: '2.5rem',
                }}
              >
                {t.partners.title}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: { xs: 3, md: 6 },
                  '& img': {
                    height: '50px',
                    width: 'auto',
                    opacity: 0.8,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      opacity: 1,
                    },
                  },
                }}
              >
                {/* Scopus */}
                {/*  <a href="https://www.scopus.com/" target="_blank" rel="noopener noreferrer">
                  <img
                    src="scopus.png"
                    alt="Scopus"
                    style={{ height: '40px' }}
                  />
                </a> */}

                {/* SCImago (SJR) */}
                {/*  <a href="https://www.scimagojr.com/" target="_blank" rel="noopener noreferrer">
                  <img
                    src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSARt0npr1kT8Z4OyZkMglgQvl7bHmOOSu2WA&s"
                    alt="SCImago Journal Rank"
                    style={{ height: '50px' }}
                  />
                </a> */}

                {/* DOAJ */}
                {/* <a href="https://doaj.org/" target="_blank" rel="noopener noreferrer">
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/thumb/9/9d/DOAJ_logo-black-whitespace.svg/500px-DOAJ_logo-black-whitespace.svg.png"
                    alt="Directory of Open Access Journals"
                    style={{ height: '50px' }}
                  />
                </a> */}

                {/* ISSN */}
                <a href="https://www.issn.org/" target="_blank" rel="noopener noreferrer">
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/thumb/0/07/ISSN_logo.svg/500px-ISSN_logo.svg.png"
                    alt="ISSN"
                    style={{ height: '40px' }}
                  />
                </a>

                {/* Open Access */}
                <a href="https://www.openaccess.nl/en" target="_blank" rel="noopener noreferrer">
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/thumb/7/77/Open_Access_logo_PLoS_transparent.svg/330px-Open_Access_logo_PLoS_transparent.svg.png"
                    alt="Open Access"
                    style={{ height: '50px' }}
                  />
                </a>

                {/* Index Copernicus */}
                {/*  <a href="https://indexcopernicus.com/index.php/en/" target="_blank" rel="noopener noreferrer">
                  <img
                    src="https://journals.indexcopernicus.com/img/ici2.png"
                    alt="Index Copernicus"
                    style={{ height: '50px' }}
                  />
                </a> */}

                {/* Creative Commons */}
                <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener noreferrer">
                  <img
                    src="creative-common.png"
                    alt="Creative Commons Attribution"
                    style={{ height: '50px' }}
                  />
                </a>

                {/* Google Scholar */}
                <a href="https://scholar.google.com/" target="_blank" rel="noopener noreferrer">
                  <img
                    src="scholar.png"
                    alt="Google Scholar"
                    style={{ height: '50px' }}
                  />
                </a>
              </Box>
            </Container>
          </Box>


          {/* FAQ Section */}
          <Box sx={{ mt: 10 }}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography variant="h4" component="h2" sx={{ fontWeight: 700, mb: 2, color: '#333', fontSize: '2.5rem' }}>
                {t.faqs.title}
              </Typography>
            </Box>

            <Grid container justifyContent="center">
              <Grid item xs={12} md={8}>
                {faqs.map((faq, index) => (
                  <Accordion key={index} sx={{ mb: 2, borderRadius: '8px !important', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', '&:before': { display: 'none' } }}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`panel${index}-content`}
                      id={`panel${index}-header`}
                      sx={{
                        backgroundColor: '#f5f5f5',
                        borderRadius: '8px',
                        '&.Mui-expanded': {
                          borderBottomLeftRadius: 0,
                          borderBottomRightRadius: 0,
                        }
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#004d40' }}>{faq.question}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" sx={{ color: '#555' }}>
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Grid>
            </Grid>
          </Box>


        </Container>
      </Box>
    </Box>
  );
};

export default Home;